import React, { useState, useEffect } from "react";
import { getSnapTradeAuthLink, getSnapTradeAccounts, deleteSnapTradeAccount } from "../../api/snaptrade";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { useAccount, useBalance, useDisconnect } from "wagmi";
import Button from "../../components/Button";

/**
 * Connected-accounts panel — rendered inline inside Settings.jsx as the
 * body of the "Connected accounts" section. The Settings shell provides
 * the page chrome, title, and sidebar; this component is just the
 * brokerage + wallet management UI.
 *
 * Visual treatment matches Settings cards: dark glassy bg, subtle border,
 * Settings-system spacing.
 */

const SectionCard = ({ children, className = "" }) => (
  <div
    className={`p-5 sm:p-6 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark ${className}`}
  >
    {children}
  </div>
);

const CryptoWalletRow = ({ walletAddress, isActive, onUnlink }) => {
  const { data: ethBalance } = useBalance({ address: walletAddress });

  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 rounded-xl border border-gray-200 dark:border-white/10 bg-white/40 dark:bg-black/20">
      <div className="flex items-center gap-4">
        <div className="w-11 h-11 rounded-xl bg-indigo-100 dark:bg-indigo-500/15 flex items-center justify-center text-xl">
          🦊
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="font-bold text-gray-900 dark:text-white">Web3 Wallet</p>
            {isActive && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase bg-emerald-500/15 text-emerald-500 border border-emerald-500/30">
                Active sync
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1.5">
            <span>Self-custodial</span>
            <span className="w-1 h-1 rounded-full bg-gray-400 dark:bg-gray-600" />
            <span className="font-mono">
              {walletAddress?.slice(0, 6)}…{walletAddress?.slice(-4)}
            </span>
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between md:justify-end gap-4 w-full md:w-auto">
        <div className="text-right">
          <p className="text-[10px] font-bold text-gray-500 dark:text-gray-500 uppercase tracking-widest mb-0.5">
            Available
          </p>
          <p className="text-base font-bold text-gray-900 dark:text-white">
            {ethBalance
              ? `${parseFloat(ethBalance.formatted).toFixed(4)} ${ethBalance.symbol}`
              : "—"}
          </p>
        </div>
        <button
          onClick={() => onUnlink(walletAddress)}
          className="px-3 py-1.5 rounded-lg text-xs font-bold bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white border border-red-500/20 transition-colors"
        >
          {isActive ? "Disconnect" : "Unlink"}
        </button>
      </div>
    </div>
  );
};

const BrokerageRow = ({ account, onDelete }) => {
  const formatCurrency = (value, currency = "USD") => {
    const n = Number(value) || 0;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(n);
  };
  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 rounded-xl border border-gray-200 dark:border-white/10 bg-white/40 dark:bg-black/20">
      <div className="flex items-center gap-4">
        <div className="w-11 h-11 rounded-xl bg-gray-100 dark:bg-white/5 flex items-center justify-center font-bold text-sm text-gray-700 dark:text-gray-300 uppercase">
          {account.brokerage ? account.brokerage.substring(0, 2) : "🏦"}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="font-bold text-gray-900 dark:text-white">
              {account.brokerage}
            </p>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase bg-emerald-500/15 text-emerald-500 border border-emerald-500/30">
              Active sync
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1.5">
            <span>{account.type || "Investment Account"}</span>
            <span className="w-1 h-1 rounded-full bg-gray-400 dark:bg-gray-600" />
            <span className="font-mono">{account.number}</span>
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between md:justify-end gap-4 w-full md:w-auto">
        <div className="text-right">
          <p className="text-[10px] font-bold text-gray-500 dark:text-gray-500 uppercase tracking-widest mb-0.5">
            Available
          </p>
          <p
            className={`text-base font-bold ${
              (account.balance || 0) < 0
                ? "text-red-500"
                : "text-gray-900 dark:text-white"
            }`}
          >
            {formatCurrency(account.balance, account.currency)}
          </p>
        </div>
        <button
          onClick={() => onDelete(account.id, account.brokerage)}
          className="px-3 py-1.5 rounded-lg text-xs font-bold bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white border border-red-500/20 transition-colors"
        >
          Unlink
        </button>
      </div>
    </div>
  );
};

const LinkedAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { address, isConnected } = useAccount();
  const { disconnect } = useDisconnect();

  const [linkedCryptoWallets, setLinkedCryptoWallets] = useState(() =>
    JSON.parse(localStorage.getItem("linkedCryptoWallets") || "[]"),
  );

  useEffect(() => {
    if (isConnected && address && !linkedCryptoWallets.includes(address)) {
      const next = [...linkedCryptoWallets, address];
      setLinkedCryptoWallets(next);
      localStorage.setItem("linkedCryptoWallets", JSON.stringify(next));
    }
  }, [isConnected, address, linkedCryptoWallets]);

  const handleUnlinkCrypto = (addrToUnlink) => {
    const next = linkedCryptoWallets.filter((a) => a !== addrToUnlink);
    setLinkedCryptoWallets(next);
    localStorage.setItem("linkedCryptoWallets", JSON.stringify(next));
    if (address === addrToUnlink) disconnect();
  };

  // Fetch on mount; re-fetch if returning from SnapTrade redirect.
  useEffect(() => {
    fetchAccounts();
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("connection_id") || urlParams.has("status")) {
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => fetchAccounts(), 2000);
    }
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await getSnapTradeAccounts();
      setAccounts(response.data.accounts || []);
    } catch (err) {
      console.error("Error fetching accounts:", err);
    }
  };

  const handleConnectBrokerage = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getSnapTradeAuthLink();
      window.location.href = response.data.auth_url;
    } catch (err) {
      console.error("Error connecting institution:", err);
      setError(
        err.response?.data?.detail ||
          "Failed to initialize secure connection portal",
      );
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (accountId, brokerageName) => {
    if (
      !window.confirm(
        `Are you sure you want to disconnect your ${brokerageName} account?`,
      )
    ) {
      return;
    }
    try {
      await deleteSnapTradeAccount(accountId);
      setAccounts(accounts.filter((acc) => acc.id !== accountId));
    } catch (err) {
      console.error("Error deleting account:", err);
      setError("Failed to disconnect institution");
    }
  };

  const empty =
    accounts.length === 0 && linkedCryptoWallets.length === 0;

  return (
    <>
      {/* Action bar — Connect Wallet + Secure Link */}
      <SectionCard>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-bold">Link a new account</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Connect a brokerage via SnapTrade, or link a self-custodial Web3
              wallet.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <ConnectButton.Custom>
              {({
                account,
                chain,
                openAccountModal,
                openChainModal,
                openConnectModal,
                mounted,
              }) => {
                const ready = mounted;
                const connected = ready && account && chain;
                return (
                  <div {...(!ready && { style: { opacity: 0 } })}>
                    {!connected ? (
                      <Button variant="secondary" onClick={openConnectModal}>
                        <svg
                          className="w-4 h-4 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                          />
                        </svg>
                        Connect Wallet
                      </Button>
                    ) : chain.unsupported ? (
                      <Button variant="secondary" onClick={openChainModal}>
                        Wrong network
                      </Button>
                    ) : (
                      <Button variant="secondary" onClick={openAccountModal}>
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                        {account.displayName}
                      </Button>
                    )}
                  </div>
                );
              }}
            </ConnectButton.Custom>
            <Button onClick={handleConnectBrokerage} disabled={loading}>
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-current/50 border-t-current rounded-full animate-spin" />
                  Initializing…
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Secure Link
                </>
              )}
            </Button>
          </div>
        </div>
      </SectionCard>

      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 flex items-center gap-3">
          <svg
            className="w-5 h-5 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="text-sm font-medium">{error}</span>
        </div>
      )}

      {/* Active connections */}
      <SectionCard>
        <h3 className="font-bold mb-1">Active connections</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Brokerages and wallets currently feeding data into your portfolio.
        </p>

        {empty ? (
          <div className="flex flex-col items-center justify-center py-10 text-center opacity-60">
            <div className="w-14 h-14 rounded-2xl bg-gray-200 dark:bg-white/5 border-2 border-dashed border-gray-400/40 dark:border-white/15 flex items-center justify-center mb-3">
              <svg
                className="w-6 h-6 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
              No institutions linked yet.
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Use "Secure Link" above to connect a brokerage.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {linkedCryptoWallets.map((addr) => (
              <CryptoWalletRow
                key={addr}
                walletAddress={addr}
                isActive={addr === address}
                onUnlink={handleUnlinkCrypto}
              />
            ))}
            {accounts.map((account) => (
              <BrokerageRow
                key={account.id}
                account={account}
                onDelete={handleDeleteAccount}
              />
            ))}
          </div>
        )}
      </SectionCard>
    </>
  );
};

export default LinkedAccounts;
